import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Divider, Dropdown, Menu, message, Modal } from 'antd';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '@hooks';
import {
  toggleBillingModal,
  toggleChangePlanDetailsModal,
  toggleReachedEntityLimitModal
} from '@redux/actions/modalAction';
import { useOrganization } from '@components/OrganizationBoundary';
import {
  CancelSubscriptionSuccessDTO,
  UpdateSubscriptionSuccessDTO
} from '@api/Api';
import {
  useCancelOrganizationSubscription,
  useUpdateOrganizationSubscription
} from '@hooks/organization';
import { fetchOrganizationById } from '@redux/actions/organizations';
import { nonNull } from '@helpers/non-null';
import {
  ArrowDownIcon,
  BillingEnterpriseIcon,
  CrossIcon,
  SuccessIcon
} from '@icons';

const plansList = [
  {
    type: 'free' as const,
    title: 'Free',
    description: 'up to 2 users, 2GB storage',
    maxUsers: 2,
    storage: '2 GB',
    price: {
      monthly: 0,
      annually: 0
    }
  },
  {
    type: 'starter' as const,
    title: 'Starter',
    description: 'up to 15 users, 500GB storage',
    maxUsers: 15,
    storage: '500 GB',
    price: {
      monthly: 219,
      annually: 169 * 12
    }
  },
  {
    type: 'team' as const,
    title: 'Team',
    description: 'up to 20 users, 1TB storage',
    maxUsers: 20,
    storage: '1 TB',
    price: {
      monthly: 369,
      annually: 299 * 12
    }
  },
  {
    type: 'enterprise' as const,
    title: 'Enterprise',
    description: '10 users minimum, custom storage',
    maxUsers: -1,
    storage: -1,
    price: {
      monthly: Number.POSITIVE_INFINITY,
      annually: Number.POSITIVE_INFINITY
    }
  }
];

function ChangePlanDetailsModal() {
  const { currentOrganization } = useOrganization();
  const [loading, setLoading] = useState<boolean>(false);
  const updateOrganizationSubscription = useUpdateOrganizationSubscription();
  const cancelOrganizationSubscription = useCancelOrganizationSubscription();
  const [plan, setPlan] = useState<'free' | 'starter' | 'team' | 'enterprise'>(
    'free'
  );
  const [method, setMethod] = useState<'monthly' | 'annually'>('monthly');

  const dispatch = useDispatch();
  const modalState = useTypedSelector(
    ({ modal }) => modal.changePlanDetailsModal
  );

  useEffect(() => {
    if (!modalState?.visible) return;
    setPlan(modalState.value);
    setMethod(modalState.paymentMethod);
  }, [modalState]);

  const onUpdateSubscription = async () => {
    if (!currentOrganization.entity) return;
    const organizationId = currentOrganization.entity.id;
    try {
      setLoading(true);
      let result: CancelSubscriptionSuccessDTO | UpdateSubscriptionSuccessDTO;
      if (plan === 'free') {
        result = await cancelOrganizationSubscription({
          organizationId
        });
      } else {
        result = await updateOrganizationSubscription({
          organizationId,
          period: method,
          type: plan as 'starter' | 'team'
        });
      }
      if ('paymentUrl' in result && result.paymentUrl) {
        window.open(result.paymentUrl, '_self');
        return;
      }
      if ('cancelUrl' in result && result.cancelUrl) {
        window.open(result.cancelUrl, '_self');
        return;
      }
      dispatch(fetchOrganizationById({ organizationId, fetchType: 'refresh' }));
      message.success({
        key: 'change-plan-modal',
        content: `Welcome to the StreamWork ${result.subscription.name} plan!`,
        icon: <SuccessIcon className="anticon" />,
        className: 'message-dark-modal',
        duration: 3
      });
    } catch (err) {
      const errorCode = (err as any).response?.data?.errorCode;
      let entity = 'unknown';
      if (errorCode === 'assets_size_limit_exceeded') entity = 'assets size';
      if (errorCode === 'subscription_users_limit_error') entity = 'user';

      dispatch(
        toggleReachedEntityLimitModal({
          visible: true,
          entity: entity as any,
          hideUpgradeButton: true
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const planItem = useMemo(
    () => nonNull(plansList.find((item) => item.type === plan)),
    [plan]
  );

  const totalCharge = planItem.price[method];

  const planType = currentOrganization.entity?.planType ?? 'free';
  const planStatus = currentOrganization.entity?.planStatus ?? 'active';
  const planPeriod = currentOrganization.entity?.planPeriod ?? 'monthly';
  const planIsCanceled =
    currentOrganization.entity?.planWillBeCanceled ?? false;

  const isButtonDisabled = useMemo(() => {
    if (plan === 'enterprise') return true;
    if (planType !== plan || planPeriod !== method) {
      return false;
    }
    if (planStatus === 'trialing' || planIsCanceled) {
      return false;
    }
    return true;
  }, [planType, planStatus, planPeriod, planIsCanceled, method, plan]);

  const buttonCopy = useMemo(() => {
    if (planType !== plan || planPeriod !== method) {
      return plan === 'free' ? 'Downgrade' : 'Update';
    }
    if (planStatus === 'trialing' || planIsCanceled) {
      return 'Update';
    }
    return 'Current';
  }, [planType, planStatus, planPeriod, planIsCanceled, method, plan]);

  const usageStats = useMemo(() => {
    const result: { label: string; usage: string }[] = [];
    if (planItem.maxUsers !== -1) {
      result.push({
        label: 'seats',
        usage: `${currentOrganization.seatsCount.count}/${planItem.maxUsers}`
      });
    }
    const storageInGB =
      currentOrganization.assetsSummarySize.size / 1024 / 1024 / 1024;
    result.push({
      label: 'storage',
      usage: `${storageInGB.toFixed(2)} GB / ${planItem.storage}`
    });
    return result;
  }, [currentOrganization, planItem]);

  return (
    <Modal
      visible={modalState?.visible}
      footer={null}
      destroyOnClose
      centered={true}
      closeIcon={<CrossIcon />}
      width={744}
      onCancel={() => dispatch(toggleChangePlanDetailsModal(null))}
      zIndex={1016}
    >
      <div className="modal_container">
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 32, justifyContent: 'center' }}
        >
          Update your StreamWork plan
        </h2>
        <div style={{ display: 'flex' }}>
          <div className="update_plan_actions">
            <div style={{ paddingBottom: 8 }}>StreamWork Plan</div>
            <Dropdown
              className="select_plan"
              overlayClassName="select_plan_overlay"
              trigger={['click']}
              overlay={
                <Menu style={{ minWidth: 300 }} selectedKeys={[plan]}>
                  {plansList.map((item) => (
                    <Menu.Item
                      key={item.type}
                      onClick={() => setPlan(item.type)}
                    >
                      <div className="title">{item.title}</div>
                      <div className="description"> • {item.description}</div>
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <span>
                <span style={{ textTransform: 'capitalize' }}>{plan}</span>
                <ArrowDownIcon className="arrow" />
              </span>
            </Dropdown>
            <div className="subscription-title-container">
              <span>Subscription</span>
              {/* <span className="b-tag">Annually -10%</span> */}
            </div>
            <Dropdown
              className="select_plan"
              trigger={['click']}
              overlay={
                <Menu style={{ minWidth: 300 }} selectedKeys={[method]}>
                  <Menu.Item
                    key="monthly"
                    onClick={({ key }) => setMethod(key as any)}
                  >
                    Monthly
                  </Menu.Item>
                  <Menu.Item
                    key="annually"
                    onClick={({ key }) => setMethod(key as any)}
                  >
                    Annually
                  </Menu.Item>
                </Menu>
              }
            >
              <span>
                <span style={{ textTransform: 'capitalize' }}>{method}</span>
                <ArrowDownIcon className="arrow" />
              </span>
            </Dropdown>
          </div>
          <div className="update_plan_information">
            {plan !== 'enterprise' && (
              <div className="stats">
                You are using{' '}
                {usageStats.map((item, index) => (
                  <Fragment key={index}>
                    <strong>{item.usage}</strong> {item.label}
                    {index < usageStats.length - 1 && ' and '}
                  </Fragment>
                ))}
              </div>
            )}
            {plan === 'enterprise' ? (
              <div className="charge_block charge_block_enterprise">
                <BillingEnterpriseIcon />
                <div className="enterprise_copy">
                  Is your team larger than 10? Contact us about an enterprise
                  plan built to scale with your organization
                </div>
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    window.open('https://www.streamwork.com/contact', '_blank');
                  }}
                >
                  Chat with our sales team
                </Button>
              </div>
            ) : (
              <div className="charge_block">
                <div>
                  <div className="charge">
                    <div className="amount">${totalCharge} </div>
                    <div className="period">
                      / {method === 'monthly' ? 'month' : 'year'}
                    </div>
                  </div>
                  <div className="info">
                    <div className="copy">Total charge</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <Divider className="modal_container__divider" />
        <div className="update_plan_story">
          By clicking &quot;Update&quot;, you agree that you will be charged the
          updated fees listed above on a pro-rated basis and your subscription
          will automatically renew based on the date of your original
          subscription until cancelled. You may cancel as described in our{' '}
          <a
            href="https://www.streamwork.com/terms-of-service#term-and-termination"
            target="_blank"
            rel="noreferrer"
          >
            Term and Termination
          </a>{' '}
          section in the StreamWork Terms of Service. You also agree to the
          StreamWork{' '}
          <a
            href="https://www.streamwork.com/terms-of-service"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="https://www.streamwork.com/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          .
        </div>
        <div className="modal_container__actions" style={{ marginTop: 32 }}>
          <Button
            type="ghost"
            size="large"
            style={{
              marginRight: 'auto',
              fontSize: 16,
              height: 48,
              padding: '8px 32px'
            }}
            onClick={() => {
              dispatch(toggleBillingModal({ visible: true }));
              dispatch(toggleChangePlanDetailsModal(null));
            }}
          >
            Compare plans
          </Button>
          <Button
            type="text"
            size="large"
            style={{
              marginRight: 16,
              border: 'none',
              height: 48,
              padding: '8px 32px'
            }}
            onClick={() => dispatch(toggleChangePlanDetailsModal(null))}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            style={{
              width: 154,
              height: 48
            }}
            loading={loading}
            disabled={isButtonDisabled}
            onClick={async () => {
              await onUpdateSubscription();
              dispatch(toggleChangePlanDetailsModal(null));
            }}
          >
            {buttonCopy}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ChangePlanDetailsModal;
